/**
 * Languages NG Portal ships static translations for.
 * This is the single source of truth for the language matching: a browser
 * preference only counts as a match when its base language is listed here.
 */
export const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'vi', 'zh', 'es', 'hi', 'pt', 'tr'] as const;

/** Used when none of the browser-preferred languages is supported. */
export const FALLBACK_LANGUAGE = 'en';

/**
 * Reduces a BCP-47 / locale tag to its base language.
 * e.g. 'fr-CH' -> 'fr', 'de_DE' -> 'de', 'ZH-Hant' -> 'zh'.
 */
export function toBaseLanguage(tag: string): string {
  return (tag ?? '').toLowerCase().split(/[-_]/)[0];
}

/**
 * Picks the language to use following the browser's Accept-Language preference.
 *
 * The browser already orders `navigator.languages` by the Accept-Language
 * q-values (highest priority first), so we walk the list in order and return
 * the first entry whose base language we have a translation for. Region
 * variants map to their base language (de_*, fr_*, ...). When no preferred
 * language is supported we fall back to English.
 */
export function resolvePreferredLanguage(
  preferredLanguages: readonly string[],
  supported: readonly string[] = SUPPORTED_LANGUAGES,
  fallback: string = FALLBACK_LANGUAGE,
): string {
  for (const preferred of preferredLanguages ?? []) {
    const base = toBaseLanguage(preferred);
    if (base && supported.includes(base)) {
      return base;
    }
  }
  return fallback;
}

/**
 * Reads the browser's ordered language preferences. This is the client-side
 * equivalent of the HTTP Accept-Language header: `navigator.languages` is built
 * from the same browser language settings and keeps the priority order.
 */
export function getBrowserPreferredLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') {
    return [];
  }
  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
}

/**
 * Resolves a language code to its name in the given UI language: 'de' becomes 'German' in an
 * English portal and 'Deutsch' in a German one. Falls back to the raw code where Intl has no
 * entry, so an unknown code renders as 'de' rather than breaking the surrounding text.
 */
export function toLanguageDisplayName(
  languageCode: string | undefined,
  uiLanguage: string = FALLBACK_LANGUAGE,
): string {
  if (!languageCode) {
    return '';
  }
  try {
    const displayNames = new Intl.DisplayNames([uiLanguage || FALLBACK_LANGUAGE], {
      type: 'language',
    });
    return displayNames.of(languageCode) ?? languageCode;
  } catch {
    return languageCode;
  }
}
